import { useUser } from '@application/authentication/use_user';
import { useFeatureFlag } from '@application/feature_flags/use_feature_flag';
import { withTranslation } from '@application/i18n/with_translation';
import { UniversalConfig } from '@application/universal_config.service';
import { BreakPoints, Divider, Flex, Spacer, Text, useBreakpoint } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { Shimmer } from '@component/new_shimmer.component';
import { PageWidthLimiter } from '@component/page_width_limiter.component';
import { AsyncSignal } from '@cortex-shim/async_signal.component';
import { RouterService } from '@cortex-shim/router.service';
import { Auth } from '@feature/auth';
import { CareCompanionLaunch } from '@feature/care_companion';
import { SelectRepresentablePatientButton } from '@view/view_find_reception/_select_representable_patient_button.component';
import { useResolve, useResolveController } from '@visiba/instantiation';
import styled from 'styled-components';
import { BrowseReceptions } from './_browse_receptions';
import { ListedAtReceptions } from './_listed_at_receptions.component';
import { RecentReceptions } from './_recent_receptions.component';
import { SelectReception } from './_select_reception.component';
import { ViewFindReceptionTempNavigationContext } from './_temp_navigation.context';
import { ViewFindReceptionController } from './view_find_reception.controller';

interface ViewFindReceptionProps {
	__TEMP__onNavigate?: (route: string) => void; // To let Angular do the routing, remove this when Angular is gone!
}

export function ViewFindReception({ __TEMP__onNavigate }: ViewFindReceptionProps) {
	const controller = useResolveController(ViewFindReceptionController);
	const routerService = useResolve(RouterService);
	const universalConfig = useResolve(UniversalConfig);
	const user = useUser();
	const breakpoint = useBreakpoint();
	const featureUseRepresentatives = useFeatureFlag('PatientWeb.UseRepresentatives');
	const featureCareCompanion = useFeatureFlag('Patient.CareCompanion.HomeChatLauncher');
	const navigate = __TEMP__onNavigate ?? ((route: string) => routerService.navigateByUrl(route));

	function renderCareCompanion() {
		if (!featureCareCompanion) {
			return null;
		}
		if (user) {
			return <CareCompanionLaunch licenceHolderId={universalConfig.licenceHolderId} isAuthenticated onNavigate={navigate} />;
		}
		return (
			<Auth.SignInSheet
				returnUrl={window.location.href}
				trigger={
					<CareCompanionLaunch licenceHolderId={universalConfig.licenceHolderId} isAuthenticated={false} onNavigate={navigate} />
				}
			/>
		);
	}

	return (
		<ViewFindReceptionTempNavigationContext.Provider value={{ __TEMP__onNavigate }}>
			<AsyncSignal signal={controller.presentation} pending={<Skeleton />} pendingDelay={500}>
				{(presentation) => (
					<>
						<StyledBackground
							style={
								presentation.backgroundImageUrl != null ? { backgroundImage: `url(${presentation.backgroundImageUrl})` } : undefined
							}
						>
							<StyledSurface>
								<Flex gap={tokens.spacing.x2}>
									<Text.h1 variant='headingMedium'>
										{user
											? withTranslation('View.FindReception.HeadingHello', { name: user.firstName }) + ` 👋`
											: withTranslation('View.FindReception.HeadingWelcome', { unitName: presentation.unitName })}
									</Text.h1>
									{!user ? (
										<Flex gap={tokens.spacing.x2} align={breakpoint < BreakPoints.Medium ? 'stretch' : 'start'}>
											<Text variant='bodyRegular'>{withTranslation('View.FindReception.LoginMessage')}</Text>
											<Auth.SignInButton />
										</Flex>
									) : null}
									<Spacer vertical={tokens.spacing.x1}>
										<Divider />
									</Spacer>
									{renderCareCompanion()}
									<SelectReception />
									<ListedAtReceptions />
									<RecentReceptions />
									{user && featureUseRepresentatives && <SelectRepresentablePatientButton />}
								</Flex>
							</StyledSurface>
						</StyledBackground>
						<PageWidthLimiter>
							<Spacer vertical={tokens.spacing.x2}>
								<BrowseReceptions />
							</Spacer>
						</PageWidthLimiter>
					</>
				)}
			</AsyncSignal>
		</ViewFindReceptionTempNavigationContext.Provider>
	);
}

const StyledBackground = styled.div`
	padding: ${tokens.spacing.x4} ${tokens.spacing.x2};
	display: flex;
	justify-content: center;
	background: ${tokens.color.bg.surfaceBrand};
	background: linear-gradient(-25deg, ${tokens.color.bg.pageBrand} 0%, ${tokens.color.bg.surfaceBrand} 100%);
	background-size: cover;
	background-position: center;
	background-repeat: no-repeat;
	border-bottom: 1px solid ${tokens.color.border.muted};

	${tokens.breakpointQuery.medium} {
		padding: ${tokens.spacing.x6} ${tokens.spacing.x3};
	}
`;

const StyledSurface = styled.div`
	background-color: ${tokens.color.bg.surface};
	border-radius: ${tokens.borderRadius.medium};
	box-shadow: ${tokens.elevation.lifted};
	padding: ${tokens.spacing.x2};
	width: 100%;
	max-width: 644px;
	height: 100%;
	color: ${tokens.color.content.default};

	${tokens.breakpointQuery.medium} {
		padding: ${tokens.spacing.x3};
	}
`;

function Skeleton() {
	return (
		<StyledBackground>
			<StyledSurface>
				<Flex gap={tokens.spacing.x4}>
					<Flex gap={tokens.spacing.x2}>
						<Shimmer width={'80%'} height={tokens.spacing.x4}></Shimmer>
						<Flex gap={tokens.spacing.x1}>
							<Shimmer width={'100%'} height={tokens.spacing.x2}></Shimmer>
							<Shimmer width={'100%'} height={tokens.spacing.x2}></Shimmer>
							<Shimmer width={'60%'} height={tokens.spacing.x2}></Shimmer>
						</Flex>
					</Flex>
					<Flex gap={tokens.spacing.x2}>
						<Shimmer width={'40%'} height={tokens.spacing.x3}></Shimmer>
						<Shimmer width={'100%'} height={tokens.spacing.x2}></Shimmer>
						<Flex gap={tokens.spacing.x1}>
							<Shimmer width={'40%'} height={tokens.spacing.x2}></Shimmer>
							<Shimmer width={'100%'} height={tokens.spacing.x5}></Shimmer>
						</Flex>
						<Flex gap={tokens.spacing.x1}>
							<Shimmer width={'45%'} height={tokens.spacing.x2}></Shimmer>
							<Shimmer width={'100%'} height={tokens.spacing.x8}></Shimmer>
						</Flex>
					</Flex>
				</Flex>
			</StyledSurface>
		</StyledBackground>
	);
}
